import { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

const DEFAULT_SETTINGS = {
  logo: null,
  logoDark: null,
  favicon: null,
  watermark: null,
  homeHeaderImage: null,
  homeHeaderVideo: null,
  homeHeaderYoutubeUrl: '',
  homeHeaderMediaType: 'image',
  homeHeaderImagePresentation: 'single',
  homeHeaderSlideshowImages: [],
  recaptchaSiteKey: '',
  homeTitle: '',
  homeSubtitle: '',
  homeHeroLayout: 'padrao',
  homeNavbarLayout: 'padrao',
  homeLocationSpotsMode: 'city',
  /** 'properties' | 'condominiums' — qual coleção alimenta contagens e links da seção bento na home */
  homeLocationSpotsEntity: 'properties',
  homeLocationSpots: [
    { name: '', image: null },
    { name: '', image: null },
    { name: '', image: null },
    { name: '', image: null }
  ],
  theme: 'light',
  companyName: '',
  primaryColor: '#3B82F6',
  secondaryColor: '#10B981',
  aboutTitle: '',
  aboutDescription: '',
  about: null,
  aboutHeroTitle: '',
  aboutHeroDescription: '',
  aboutHeroImage: null,
  aboutFeaturesTitle: '',
  aboutFeaturesSubtitle: '',
  aboutFeatures: [],
  aboutStat1Name: '',
  aboutStat1Value: '',
  aboutStat2Name: '',
  aboutStat2Value: '',
  aboutStat3Name: '',
  aboutStat3Value: '',
  aboutStat4Name: '',
  aboutStat4Value: '',
  aboutVisionText: '',
  aboutVisionImage: null,
  aboutMissionText: '',
  aboutMissionImage: null,
  aboutValuesText: '',
  aboutValuesImage: null,
  aboutHeroInsight1: '',
  aboutHeroInsight2: '',
  feedbacksTitle: '',
  ctaEnabled: false,
  ctaLayout: 'withImage',
  ctaTitle: 'Pronto para encontrar seu próximo imóvel?',
  ctaDescription: 'Fale com nossa equipe e tire suas dúvidas. Estamos prontos para ajudar você.',
  ctaButtonText: 'Entrar em contato',
  ctaImageSource: 'system',
  ctaSystemImage: 'cta1',
  ctaImageStyle: 'noBackground',
  ctaImage: null,
  ctaAboutEnabled: false,
  contactPageTitle: 'Entre em Contato',
  contactPageDescription: 'Estamos prontos para ajudar você a encontrar o imóvel dos seus sonhos',
  contactHeroImage: null,
  contactHistory: '',
  contactMission: 'Conectar pessoas aos seus lares ideais através de soluções imobiliárias inovadoras e atendimento excepcional.',
  contactVision: 'Ser a imobiliária de referência na região, reconhecida pela excelência e inovação no mercado imobiliário.',
  contactValues: 'Transparência, confiança, inovação e compromisso com a satisfação dos nossos clientes.',
  contactName: 'Matriz Centro',
  contactAddress: 'Rua das Flores, 123 - Centro, São Paulo - SP',
  contactAddressStructured: {
    cep: '01234-567',
    logradouro: 'Rua das Flores',
    numero: '123',
    complemento: '',
    bairro: 'Centro',
    localidade: 'São Paulo',
    uf: 'SP'
  },
  contactPhone: '(11) 3456-7890',
  contactEmail: 'contato@imobiliaria.com.br',
  contactCep: '',
  contactLogradouro: '',
  contactNumero: '',
  contactComplemento: '',
  contactBairro: '',
  contactCidade: '',
  contactEstado: '',
  contactHours: {
    weekdays: { from: '08:00', to: '18:00', closed: false },
    saturdays: { from: '09:00', to: '16:00', closed: false },
    sundays: { from: '10:00', to: '14:00', closed: false }
  },
  contactBranchesEnabled: false,
  branches: [
    {
      id: '1',
      name: 'Filial Shopping',
      address: 'Shopping Plaza, Loja 45 - 2º Piso, São Paulo - SP',
      addressStructured: {
        cep: '04567-890',
        logradouro: 'Shopping Plaza',
        numero: 'Loja 45',
        complemento: '2º Piso',
        bairro: 'Vila Olímpia',
        localidade: 'São Paulo',
        uf: 'SP'
      },
      phone: '(11) 3456-7891',
      email: 'shopping@imobiliaria.com.br',
      hours: {
        weekdays: { from: '10:00', to: '22:00', closed: false },
        saturdays: { from: '10:00', to: '22:00', closed: false },
        sundays: { from: '14:00', to: '20:00', closed: false }
      }
    },
    {
      id: '2',
      name: 'Filial Zona Sul',
      address: 'Av. Paulista, 1000 - Zona Sul, São Paulo - SP',
      phone: '(11) 3456-7892',
      email: 'zonasul@imobiliaria.com.br',
      hours: {
        weekdays: { from: '09:00', to: '17:00', closed: false },
        saturdays: { from: '09:00', to: '13:00', closed: false },
        sundays: { from: '', to: '', closed: true }
      }
    }
  ],
  seoMetaTitle: '',
  seoMetaDescription: '',
  seoKeywords: '',
  seoOgTitle: '',
  seoOgDescription: '',
  seoOgImage: null,
  seoUseLogoForOgImage: true,
  seoGoogleAnalytics: '',
  seoGoogleTagManager: '',
  seoFacebookPixel: '',
  seoCanonicalUrl: '',
  seoBusinessType: 'RealEstateAgent',
  seoServiceArea: '',
  seoPriceRange: '$$',
  seoOpeningHours: '',
  footerBackgroundColor: '#111827',
  privacyPolicyContent: '',
  privacyPolicyLastUpdated: '',
  termsOfUseContent: '',
  termsOfUseLastUpdated: ''
};

function readCachedSettings() {
  try {
    const raw = localStorage.getItem('companySettings');
    if (!raw) return { ...DEFAULT_SETTINGS };
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

/** Cache de sessão: um único getDoc(settings/main) compartilhado entre todos os hooks. */
let sessionSettings = null;
let sessionReady = false;
let inflightPromise = null;
const subscribers = new Set();

function notifySubscribers() {
  subscribers.forEach((cb) => {
    try {
      cb(sessionSettings, sessionReady);
    } catch {
      // ignore subscriber errors
    }
  });
}

function fetchCompanySettingsOnce() {
  if (sessionSettings && sessionReady && !inflightPromise) {
    return Promise.resolve(sessionSettings);
  }
  if (inflightPromise) return inflightPromise;

  inflightPromise = (async () => {
    try {
      const db = getFirestore();
      const snap = await getDoc(doc(db, 'settings', 'main'));
      if (snap.exists()) {
        const merged = { ...(sessionSettings || readCachedSettings()), ...snap.data() };
        sessionSettings = merged;
        try {
          localStorage.setItem('companySettings', JSON.stringify(merged));
        } catch {
          // quota / private mode
        }
      } else if (!sessionSettings) {
        sessionSettings = readCachedSettings();
      }
    } catch {
      if (!sessionSettings) {
        sessionSettings = readCachedSettings();
      }
    } finally {
      sessionReady = true;
      inflightPromise = null;
      notifySubscribers();
    }
    return sessionSettings;
  })();

  return inflightPromise;
}

export function useCompanySettings() {
  const [settings, setSettings] = useState(() => {
    if (sessionSettings) return sessionSettings;
    const cached = readCachedSettings();
    sessionSettings = cached;
    return cached;
  });
  const [settingsReady, setSettingsReady] = useState(() => {
    if (sessionReady) return true;
    try {
      return Boolean(localStorage.getItem('companySettings'));
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const onUpdate = (nextSettings, ready) => {
      if (nextSettings) setSettings(nextSettings);
      setSettingsReady(ready);
    };
    subscribers.add(onUpdate);
    if (sessionSettings) setSettings(sessionSettings);
    if (sessionReady) setSettingsReady(true);
    fetchCompanySettingsOnce();
    return () => {
      subscribers.delete(onUpdate);
    };
  }, []);

  const getFullAddress = () => {
    const parts = [];
    if (settings.contactLogradouro) parts.push(settings.contactLogradouro);
    if (settings.contactNumero) parts.push(settings.contactNumero);
    if (settings.contactComplemento) parts.push(settings.contactComplemento);
    if (settings.contactBairro) parts.push(settings.contactBairro);
    if (settings.contactCidade) parts.push(settings.contactCidade);
    if (settings.contactEstado) parts.push(settings.contactEstado);
    if (settings.contactCep) parts.push(settings.contactCep);
    return parts.join(', ');
  };

  const getMapsLink = () => {
    const address = getFullAddress() || settings.contactAddress;
    if (!address) return null;
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  };

  return {
    ...settings,
    settingsReady,
    fullAddress: getFullAddress(),
    mapsLink: getMapsLink()
  };
}
